/**
 * What a locked feature looks like where commerce is off (see
 * src/lib/commerce/policy.ts). It may say a feature is not part of the user's
 * plan. It may not show a price, name a purchase, or point anywhere a purchase
 * happens — so it has no button and no link, by construction.
 *
 * Variants mirror UpgradeOffer's so a host that renders one or the other keeps
 * its layout: `fullscreen` fills its parent; `card` and `banner` size to content.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Lock } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { NOT_IN_PLAN } from '@/lib/commerce/policy';

export interface PlanLockedNoticeProps {
  variant: 'banner' | 'card' | 'fullscreen';
  /** A noun phrase — "Portfolio tracking", never "Unlock portfolio tracking". */
  title: string;
}

export function PlanLockedNotice({ variant, title }: PlanLockedNoticeProps) {
  const colors = useThemeColors();
  const userRole = useSubscriptionStore((s) => s.userRole);

  if (userRole === 'admin') return null;

  if (variant === 'banner') {
    return (
      <View style={[styles.banner, { backgroundColor: colors.surface.secondary, borderColor: colors.border.light }]}>
        <Lock size={16} color={colors.text.tertiary} />
        <View style={styles.bannerText}>
          <Text numberOfLines={1} style={[styles.bannerTitle, { color: colors.text.primary }]}>{title}</Text>
          <Text numberOfLines={1} style={[styles.bannerSubtitle, { color: colors.text.tertiary }]}>{NOT_IN_PLAN}</Text>
        </View>
      </View>
    );
  }

  const fullscreen = variant === 'fullscreen';
  return (
    <Animated.View entering={FadeInUp.duration(400)} style={fullscreen ? styles.fullscreenOuter : undefined}>
      <View
        style={[
          fullscreen ? styles.fullscreenCard : styles.card,
          { backgroundColor: colors.surface.primary, borderColor: colors.border.light },
        ]}
      >
        <View style={[fullscreen ? styles.iconLarge : styles.icon, { backgroundColor: colors.surface.secondary }]}>
          <Lock size={fullscreen ? 32 : 20} color={colors.text.secondary} />
        </View>
        <Text numberOfLines={2} style={[fullscreen ? styles.titleLarge : styles.title, { color: colors.text.primary }]}>
          {title}
        </Text>
        <Text numberOfLines={2} style={[styles.subtitle, { color: colors.text.secondary }]}>{NOT_IN_PLAN}</Text>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  banner: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 12, borderRadius: 14, borderWidth: 1 },
  bannerText: { flex: 1 },
  bannerTitle: { fontSize: 14, fontWeight: '600' },
  bannerSubtitle: { fontSize: 12, marginTop: 2 },
  card: { borderRadius: 16, borderWidth: 1, padding: 20, alignItems: 'center', overflow: 'hidden' },
  icon: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  title: { fontSize: 16, fontWeight: '700', textAlign: 'center', marginBottom: 4 },
  subtitle: { fontSize: 13, textAlign: 'center', lineHeight: 18 },
  fullscreenOuter: { flex: 1, padding: 24 },
  fullscreenCard: { flex: 1, borderRadius: 24, borderWidth: 1, padding: 32, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  iconLarge: { width: 72, height: 72, borderRadius: 36, alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  titleLarge: { fontSize: 22, fontWeight: '700', textAlign: 'center', marginBottom: 8 },
});
